import type { ShallowDehydrateObject } from 'kysely'
import { z } from 'zod'

import type {
  ModemInitializer,
  ModemHistory,
  ModemHistoryInitializer,
  Modem,
  ModemHistoryId,
  ModemHistoryMutator,
  ModemMutator,
  ModemId,
} from '../../index.js'
import {
  modemInitializer,
  modemHistoryInitializer,
  modemHistory,
  modem,
  modemHistoryMutator,
  modemMutator,
} from '../../index.js'

export type ModemWithContextInitializer = ModemInitializer & {
  history: Omit<ModemHistoryInitializer, 'modemId'>[]
}

type OmmittedKeys =
  | 'modemId'
  | 'createdAt'
  | 'createdBy'
  | 'updatedAt'
  | 'updatedBy'
  | 'deletedAt'
  | 'deletedBy'

// Embedded via kysely's jsonArrayFrom: postgres json_agg serializes timestamps
// to ISO strings and node-postgres does not revive them, so the date columns
// arrive as strings. ShallowDehydrateObject is the same transformation the
// query builder applies, so this type cannot drift from the real row shape.
export type EmbeddedModemHistory = ShallowDehydrateObject<
  Omit<ModemHistory, OmmittedKeys>
> & {
  loggerSerial: string
}

export type ModemFilter = {
  modemId?: ModemId
  imei?: string
}

export type MessagesPerTimeRange = {
  messages: number
  useId?: ModemHistoryId
  range: {
    // startedAt/endedAt are copied from EmbeddedModemHistory, so they are
    // already json-dehydrated ISO strings. inferredStart/inferredEnd instead
    // come from mongo `ts.email`, which the driver returns as a real Date --
    // it only becomes a string once this crosses the wire as JSON.
    inferredStart?: Date | string
    startedAt?: string
    endedAt?: string
    inferredEnd?: Date | string
  }
}

export type ModemWithContext = Modem & {
  history: EmbeddedModemHistory[]
  networkStatus: string
  messagesPerTimeRange?: MessagesPerTimeRange[]
}

export const modemWithContextInitializer = z.object({
  history: z.array(modemHistoryInitializer).optional(),
  ...(modemInitializer as unknown as z.ZodObject<any>).shape,
}) as unknown as z.Schema<ModemWithContextInitializer>

export const modemWithContext = z.object({
  history: z.array(
    z.object({
      loggerSerial: z.string(),
      ...(modemHistory as unknown as z.ZodObject<any>).shape,
    }),
  ),
  networkStatus: z.string(),
  ...(modem as unknown as z.ZodObject<any>).shape,
}) as unknown as z.Schema<ModemWithContext>

export type ModemWithContextMutator = ModemMutator & {
  history?: ModemHistoryMutator[]
}

export const modemWithContextMutator = z.object({
  history: z.array(modemHistoryMutator).optional(),
  ...(modemMutator as unknown as z.ZodObject<any>).shape,
}) as unknown as z.Schema<ModemWithContextMutator>
